/**
 * Make docx-preview's list numbers match Word's.
 *
 * docx-preview numbers each list instance (w:num) with its own CSS counters,
 * starting at `start - 1`. Word counts differently, which contracts rely on:
 * - Instances of the same list definition (w:abstractNum) share one count, so
 *   "2.1 Fees" (instance 13) is followed by "2.4 Late Payments" (instance 41),
 *   where docx-preview restarted at "1.1" and showed "2.1".
 * - A level with no paragraph of its own shows its start value: a list that
 *   begins at "%1.%2.%3" reads "5.1.1" in Word, "4.0.1" in docx-preview.
 * - An instance's start override restarts its own level the first time that
 *   instance numbers a paragraph at that level ("Restart numbering").
 * - docx-preview styles only one instance per list definition (the last
 *   w:num referencing it); paragraphs on the others got no number at all, so
 *   most RWS clauses (list 41, 13, 9, …) showed none. listStylesForAllInstances
 *   copies its number styles to the other instances.
 * - A paragraph numbered by its style takes the nearest style's list. docx-
 *   preview merges a parent style's numbering over the child's, so headings
 *   styled "Article" (list 41) showed list 1 from their parent "Heading2".
 *   withParagraphNumbering writes Word's list onto each paragraph first.
 *
 * So the numbers are computed here as Word does and pinned on each numbered
 * paragraph with counter-set; docx-preview's ::before still formats them.
 * (Checked against Word on the web with the RWS MSA, Oct 2026.)
 */

export interface ListDefinitions {
  /** w:num id -> w:abstractNum id */
  abstractOf: Map<string, string>;
  /** w:abstractNum id -> start value per level */
  starts: Map<string, number[]>;
  /** w:num id -> level -> start override */
  overrides: Map<string, Map<number, number>>;
  /** w:abstractNum id -> paragraph style id -> the level that style numbers */
  styleLevels: Map<string, Map<string, number>>;
  /** w:abstractNum id -> its w:num ids, in file order (docx-preview styles the last) */
  instances: Map<string, string[]>;
}

const attr = (el: Element | undefined | null, name: string) =>
  el?.getAttribute(`w:${name}`) ?? el?.getAttribute(name) ?? null;

const children = (el: Element, tag: string) =>
  Array.from(el.getElementsByTagName(`w:${tag}`));

/** The list definitions in word/numbering.xml */
export function parseNumbering(xml: string): ListDefinitions {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const defs: ListDefinitions = {
    abstractOf: new Map(),
    starts: new Map(),
    overrides: new Map(),
    styleLevels: new Map(),
    instances: new Map(),
  };
  for (const abs of children(doc.documentElement, 'abstractNum')) {
    const id = attr(abs, 'abstractNumId');
    if (id === null) continue;
    const starts: number[] = [];
    const styleLevels = new Map<string, number>();
    for (const lvl of children(abs, 'lvl')) {
      const level = Number(attr(lvl, 'ilvl'));
      const start = Number(attr(children(lvl, 'start')[0], 'val') ?? 0);
      if (!Number.isInteger(level)) continue;
      starts[level] = Number.isFinite(start) ? start : 0;
      const pStyle = attr(children(lvl, 'pStyle')[0], 'val');
      if (pStyle) styleLevels.set(pStyle, level);
    }
    defs.starts.set(id, starts);
    defs.styleLevels.set(id, styleLevels);
  }
  for (const num of children(doc.documentElement, 'num')) {
    const id = attr(num, 'numId');
    const abstractId = attr(children(num, 'abstractNumId')[0], 'val');
    if (id === null || abstractId === null) continue;
    defs.abstractOf.set(id, abstractId);
    defs.instances.set(abstractId, [...(defs.instances.get(abstractId) ?? []), id]);
    const overrides = new Map<number, number>();
    for (const o of children(num, 'lvlOverride')) {
      const start = attr(children(o, 'startOverride')[0], 'val');
      if (start !== null) overrides.set(Number(attr(o, 'ilvl')), Number(start));
    }
    if (overrides.size > 0) defs.overrides.set(id, overrides);
  }
  return defs;
}

/**
 * Word's value for every level of each numbered paragraph, in document order.
 * null for a paragraph whose list isn't defined (left to docx-preview).
 */
export function wordListValues(
  paragraphs: Array<{ numId: string; level: number }>,
  defs: ListDefinitions,
): Array<number[] | null> {
  const counters = new Map<string, number[]>(); // per abstractNum
  const overridden = new Set<string>(); // `${numId}:${level}` already applied
  return paragraphs.map(({ numId, level }) => {
    const abstractId = defs.abstractOf.get(numId);
    const starts = abstractId !== undefined ? defs.starts.get(abstractId) : undefined;
    if (abstractId === undefined || !starts) return null;
    const overrides = defs.overrides.get(numId);
    const startOf = (k: number) => overrides?.get(k) ?? starts[k] ?? 1;

    const values = counters.get(abstractId) ?? [];
    counters.set(abstractId, values);
    const key = `${numId}:${level}`;
    if (overrides?.has(level) && !overridden.has(key)) {
      overridden.add(key);
      values[level] = overrides.get(level)!;
    } else {
      values[level] = (values[level] ?? startOf(level) - 1) + 1;
    }
    // A paragraph at this level restarts the levels below it
    values.length = level + 1;

    const shown: number[] = [];
    for (let k = 0; k <= level; k++) shown[k] = values[k] ?? startOf(k);
    return shown;
  });
}

/**
 * Pin Word's numbers on the numbered paragraphs docx-preview rendered
 * (class `<prefix>-num-<numId>-<level>`). Hidden paragraphs don't count.
 * Returns how many paragraphs were numbered.
 */
export function applyWordListNumbers(
  container: HTMLElement,
  defs: ListDefinitions,
  prefix = 'docx-preview',
): number {
  const numClass = new RegExp(`^${prefix}-num-(\\d+)-(\\d+)$`);
  const numbered: Array<{ el: HTMLElement; numId: string; level: number }> = [];
  container.querySelectorAll<HTMLElement>('article p').forEach((el) => {
    if (el.style.display === 'none') return;
    const match = Array.from(el.classList)
      .map((c) => c.match(numClass))
      .find(Boolean);
    if (match) numbered.push({ el, numId: match[1], level: Number(match[2]) });
  });

  const values = wordListValues(numbered, defs);
  let applied = 0;
  numbered.forEach(({ el, numId, level }, i) => {
    const shown = values[i];
    if (!shown) return;
    // ::before adds 1 to the paragraph's own level, so set that one to n - 1
    const sets = shown.map(
      (v, k) => `${prefix}-num-${numId}-${k} ${k === level ? v - 1 : v}`,
    );
    el.style.setProperty('counter-set', sets.join(' '));
    applied++;
  });
  return applied;
}

const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

/** A paragraph properties' own w:numPr: list id and level, each optional */
function numPrOf(pPr: Element | undefined) {
  const numPr = pPr ? children(pPr, 'numPr')[0] : undefined;
  if (!numPr) return null;
  const ilvl = attr(children(numPr, 'ilvl')[0], 'val');
  return {
    numId: attr(children(numPr, 'numId')[0], 'val'),
    level: ilvl === null ? null : Number(ilvl),
  };
}

/**
 * document.xml with each paragraph that is numbered through its style given
 * that numbering explicitly, resolved as Word does: the paragraph's own
 * numPr, then the nearest style up the basedOn chain that has one; a missing
 * level comes from the list level linked to that style, else 0. Nothing else
 * changes, so the rendered text and formatting stay the same.
 */
export function withParagraphNumbering(
  documentXml: string,
  stylesXml: string,
  defs: ListDefinitions,
): string {
  const parser = new DOMParser();
  const styles = parser.parseFromString(stylesXml, 'application/xml');
  const styleById = new Map<string, Element>();
  for (const st of children(styles.documentElement, 'style')) {
    const id = attr(st, 'styleId');
    if (id && attr(st, 'type') === 'paragraph') styleById.set(id, st);
  }
  const defaultStyle = Array.from(styleById.values()).find(
    (st) => attr(st, 'type') === 'paragraph' && attr(st, 'default') === '1',
  );

  /** Nearest numPr up the style chain, with the style that set it */
  const styleNumbering = (styleId: string | null) => {
    const seen = new Set<string>();
    let id = styleId ?? (defaultStyle ? attr(defaultStyle, 'styleId') : null);
    let numId: string | null = null;
    let level: number | null = null;
    let numberingStyle: string | null = null;
    while (id && !seen.has(id)) {
      seen.add(id);
      const st = styleById.get(id);
      if (!st) break;
      const own = numPrOf(children(st, 'pPr')[0]);
      if (own) {
        if (level === null && own.level !== null) level = own.level;
        if (own.numId !== null) {
          numId = own.numId;
          numberingStyle = id;
          break;
        }
      }
      id = attr(children(st, 'basedOn')[0], 'val');
    }
    return { numId, level, numberingStyle };
  };

  const doc = parser.parseFromString(documentXml, 'application/xml');
  let changed = false;
  for (const p of children(doc.documentElement, 'p')) {
    const pPr = Array.from(p.childNodes).find(
      (n): n is Element => n.nodeType === 1 && (n as Element).localName === 'pPr',
    );
    const own = numPrOf(pPr);
    // Numbered explicitly already, or numbering switched off (numId 0)
    if (own?.numId === '0') continue;
    if (own?.numId != null && own.level !== null) continue;
    const styleId = pPr ? attr(children(pPr, 'pStyle')[0], 'val') : null;
    const fromStyle = styleNumbering(styleId);
    const numId = own?.numId ?? fromStyle.numId;
    if (numId === null || numId === undefined) continue;
    const abstractId = defs.abstractOf.get(numId);
    const level =
      own?.level ??
      fromStyle.level ??
      (abstractId !== undefined && fromStyle.numberingStyle
        ? defs.styleLevels.get(abstractId)?.get(fromStyle.numberingStyle)
        : undefined) ??
      0;

    const props = pPr ?? p.insertBefore(doc.createElementNS(W_NS, 'w:pPr'), p.firstChild);
    for (const old of children(props, 'numPr')) old.parentNode?.removeChild(old);
    const numPr = doc.createElementNS(W_NS, 'w:numPr');
    const ilvl = doc.createElementNS(W_NS, 'w:ilvl');
    ilvl.setAttributeNS(W_NS, 'w:val', String(level));
    const num = doc.createElementNS(W_NS, 'w:numId');
    num.setAttributeNS(W_NS, 'w:val', numId);
    numPr.appendChild(ilvl);
    numPr.appendChild(num);
    // After pStyle (and keepNext etc. don't matter to the renderer)
    const pStyle = children(props, 'pStyle')[0];
    props.insertBefore(numPr, pStyle ? pStyle.nextSibling : props.firstChild);
    changed = true;
  }
  return changed ? new XMLSerializer().serializeToString(doc) : documentXml;
}

/**
 * CSS giving every instance of a list definition the number styles docx-
 * preview generated for the one instance it handles (the last w:num of the
 * definition): the ::before format, font and indent. The root counter-reset
 * is left alone (applyWordListNumbers sets every value explicitly).
 */
export function listStylesForAllInstances(
  cssText: string,
  defs: ListDefinitions,
  prefix = 'docx-preview',
): string {
  const rules = cssText.match(/[^{}]+\{[^{}]*\}/g) ?? [];
  const out: string[] = [];
  defs.instances.forEach((numIds, abstractId) => {
    if (numIds.length < 2) return;
    const handled = numIds[numIds.length - 1];
    const marker = `${prefix}-num-${handled}-`;
    const own = rules.filter((r) => r.includes(marker) && !r.includes('counter-reset'));
    if (own.length === 0) return;
    for (const numId of numIds.slice(0, -1)) {
      for (const rule of own) out.push(rule.split(marker).join(`${prefix}-num-${numId}-`).trim());
    }
  });
  return out.join('\n');
}
